import React from 'react';
import PropTypes from 'prop-types';
import { Spacing, Text, Icon } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import passengerTypes from '../../config/passengerTypes';
import QuantitySelector from '../atoms/QuantitySelector';

/**
 * PassengerQuantitySelector component.
 * @param {object} props - variant, type, onClickIncrement, onClickDecrement, counter, isDisabled, iconSize.
 * @param {boolean} props.variant - Indicates if it's a variant to adjust styles.
 * @param {string} props.type - The passenger category.
 * @param {function} props.onClickIncrement - Function to increment the counter.
 * @param {function} props.onClickDecrement - Function to decrement the counter.
 * @param {number} props.counter - The current passenger count.
 * @param {boolean} props.isDisabled - Indicates if the selector is disabled.
 * @param {('S'|'M')} props.iconSize - Size of the passenger icon ('S' by default or 'M').
 */
function PassengerQuantitySelector({
  variant,
  type,
  onClickIncrement,
  onClickDecrement,
  counter,
  isDisabled,
  iconSize = 'S',
}) {
  const { t } = useTranslation('passengers');
  const { icon, iconVariant, label, disclaimer } = passengerTypes[type];

  return (
    <Spacing justifyContent="space-between" alignItems="center">
      <Spacing size="XS" alignItems="center">
        <Icon type={variant ? iconVariant : icon} size={iconSize} />

        <Spacing size="XXS" vertical>
          <Text weight="bold" mobileSize="S">
            {t(label)}
          </Text>
          {disclaimer && <Text size="S">{t(disclaimer)}</Text>}
        </Spacing>
      </Spacing>

      <QuantitySelector
        onClickIncrement={onClickIncrement}
        onClickDecrement={onClickDecrement}
        counter={counter}
        isIncreaseDisabled={isDisabled}
        isDecreaseDisabled={counter === 0}
      />
    </Spacing>
  );
}

PassengerQuantitySelector.propTypes = {
  type: PropTypes.oneOf(Object.keys(passengerTypes)).isRequired,
  variant: PropTypes.bool,
  iconSize: PropTypes.oneOf(['S', 'L']),
  onClickIncrement: PropTypes.func,
  onClickDecrement: PropTypes.func,
  counter: PropTypes.number,
  isDisabled: PropTypes.bool,
};

PassengerQuantitySelector.defaultProps = {
  variant: false,
  iconSize: 'S',
};

export default PassengerQuantitySelector;
